import React, { useEffect, useRef, useState } from "react";
import { TimeInput, TimeInputProps } from "@mantine/dates";
import { ActionIcon } from "@mantine/core";
import CustomTimePopover from "./custom-time-popover";
import styles from "@scss/custom-components/form-fields-dropdowns.module.scss";

function useCombinedRefs<T>(forwardedRef?: React.Ref<T>) {
  const targetRef = useRef<T | null>(null);

  useEffect(() => {
    if (!forwardedRef) return;
    if (typeof forwardedRef === "function") {
      forwardedRef(targetRef.current as T);
    } else if (typeof forwardedRef === "object" && forwardedRef !== null) {
      (forwardedRef as React.MutableRefObject<T | null>).current =
        targetRef.current;
    }
  }, [forwardedRef]);

  return targetRef as React.MutableRefObject<T | null>;
}

export const CustomTimeInput = React.forwardRef<
  HTMLInputElement,
  TimeInputProps & { maxH?: number | string; maxMin?: number | string }
>((props, forwardedRef) => {
  const localRef = useCombinedRefs<HTMLInputElement>(forwardedRef);
  const [internalValue, setInternalValue] = useState<Date | null>(null);

  const {
    rightSection: userRightSection,
    label,
    value: propValue,
    onChange: propOnChange,
    maxH,
    maxMin,
    ...rest
  } = props as TimeInputProps & {
    value?: Date | null;
    maxH?: number | string;
    maxMin?: number | string;
  };

  // detect a variant passed via props (mantine uses data-variant on root, but theme may set classNames)
  const variant =
    (rest as any)["variant"] || (rest as any)["data-variant"] || "default";

  const effectiveValue =
    propValue === undefined ? internalValue : (propValue ?? null);

  const handlePickerChange = (v: Date | null) => {
    // debug: confirm popover selection reaches this handler
    // eslint-disable-next-line no-console
    console.debug("[CustomTimeInput] handlePickerChange", v);
    setInternalValue(v);

    if (!propOnChange) return;

    // Only call propOnChange if it expects a Date | null
    if (typeof propOnChange === "function") {
      try {
        (propOnChange as unknown as (d: Date | null) => void)(v);
      } catch (e) {
        try {
          (propOnChange as unknown as any)(v);
        } catch (_) {
          console.error(
            "[CustomTimeInput] Failed to call onChange with Date, falling back to null",
            _,
          );
        }
      }
    }
  };

  // format Date -> "HH:mm" for native TimeInput
  const pad2 = (n: number) => n.toString().padStart(2, "0");
  const formatToTimeString = (d: Date | null) =>
    d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : "";

  // when TimeInput (native) changes, it may provide a string like "HH:mm" or a Date
  const handleNativeChange = (v: any) => {
    if (v == null) {
      handlePickerChange(null);
      return;
    }
    if (v instanceof Date) {
      handlePickerChange(v);
      return;
    }
    if (typeof v === "string") {
      const parts = v.split(":");
      if (parts.length >= 2) {
        const hh = parseInt(parts[0], 10);
        const mm = parseInt(parts[1], 10);
        const next = effectiveValue ? new Date(effectiveValue) : new Date();
        next.setHours(hh, mm, 0, 0);
        handlePickerChange(next);
        return;
      }
    }
    // fallback
    try {
      handlePickerChange(v as Date);
    } catch (_) {
      handlePickerChange(null);
    }
  };

  // Build a TimeInput element that will be used as the popover target so clicking the field opens the popover
  const timeInputElement = (
    <TimeInput
      label={label ?? "Click field to show time picker"}
      ref={localRef}
      rightSection={
        userRightSection ?? (
          <ActionIcon
            size="xs"
            aria-label={typeof label === "string" ? label : "Open time picker"}
            className={styles.clockButton}
          >
            <></>
          </ActionIcon>
        )
      }
      {...(rest as TimeInputProps)}
      value={formatToTimeString(effectiveValue) as any}
      onChange={handleNativeChange as any}
    />
  );

  return (
    <CustomTimePopover
      value={effectiveValue}
      onChange={handlePickerChange}
      variant={variant}
      maxH={maxH}
      maxMin={maxMin}
      target={timeInputElement}
    />
  );
});

CustomTimeInput.displayName = "CustomTimeInput";
